import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import './SurveyFill.css';
import Button from './Button';
import { UserContext } from '../contexts/UserContext';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon,
  Flag as FlagIcon,
  OutlinedFlag as FlagBorderIcon,
  ThumbUp as ThumbIcon,
  ThumbUpOutlined as ThumbBorderIcon,
  SentimentSatisfied as SmileIcon,
  SentimentSatisfiedOutlined as SmileBorderIcon,
  EmojiEvents as RibbonIcon,
  EmojiEventsOutlined as RibbonBorderIcon,
  DragIndicator as DragIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Work as ProjectIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const SurveyFill = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [selectOptions, setSelectOptions] = useState({ users: [], projects: [] });
  const [searchQueries, setSearchQueries] = useState({}); // Search queries per question

  useEffect(() => {
    fetchSurvey();
    fetchSelectOptions();
  }, [surveyId]);

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchSelectOptions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/select-options/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectOptions(response.data);
    } catch (error) {
      console.error('Error fetching select options:', error);
    }
  };

  const fetchSurvey = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/fill/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSurvey(response.data);

      if (response.data.is_completed) {
        setSubmitted(true);
      }

      // Initialize answers
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        if (q.question_type === 'choice') {
          initialAnswers[q.id] = q.allow_multiple ? [] : null;
        } else if (q.question_type === 'text') {
          initialAnswers[q.id] = '';
        } else if (q.question_type === 'rating' || q.question_type === 'nps') {
          initialAnswers[q.id] = null;
        } else if (q.question_type === 'date') {
          initialAnswers[q.id] = '';
        } else if (q.question_type === 'ranking') {
          initialAnswers[q.id] = q.options.map(o => o.id);
        } else if (q.question_type === 'likert') {
          initialAnswers[q.id] = {};
        } else if (q.question_type === 'file') {
          initialAnswers[q.id] = null;
        } else if (q.question_type === 'user_select') {
          initialAnswers[q.id] = [];
        } else if (q.question_type === 'project_select') {
          initialAnswers[q.id] = [];
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Error fetching survey:', error);
      if (error.response?.status === 403) {
        showToastNotification('You are not assigned to this survey');
      } else {
        showToastNotification('Error loading survey');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateAnswers = () => {
    const newErrors = {};
    let isValid = true;

    survey.questions.forEach(q => {
      if (q.is_required && q.question_type !== 'section') {
        const answer = answers[q.id];
        let isEmpty = false;

        if (q.question_type === 'choice') {
          isEmpty = q.allow_multiple ? answer.length === 0 : answer === null;
        } else if (q.question_type === 'text') {
          isEmpty = !answer || answer.trim() === '';
        } else if (q.question_type === 'rating' || q.question_type === 'nps') {
          isEmpty = answer === null;
        } else if (q.question_type === 'date') {
          isEmpty = !answer;
        } else if (q.question_type === 'likert') {
          isEmpty = Object.keys(answer).length < q.statements.length;
        } else if (q.question_type === 'user_select' || q.question_type === 'project_select') {
          isEmpty = !answer || answer.length === 0;
        }

        if (isEmpty) {
          newErrors[q.id] = 'This question is required';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateAnswers()) {
      showToastNotification('Please answer all required questions');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');

      // Format answers for API
      const formattedAnswers = Object.entries(answers)
        .filter(([questionId, _]) => {
          const question = survey.questions.find(q => q.id === parseInt(questionId));
          return question && question.question_type !== 'section';
        })
        .map(([questionId, value]) => {
          const question = survey.questions.find(q => q.id === parseInt(questionId));
          const answerData = { question_id: parseInt(questionId) };

          if (question.question_type === 'text') {
            answerData.text_answer = value;
          } else if (question.question_type === 'choice') {
            answerData.choice_answers = question.allow_multiple ? value : (value ? [value] : []);
          } else if (question.question_type === 'rating' || question.question_type === 'nps') {
            answerData.rating_answer = value;
          } else if (question.question_type === 'date') {
            answerData.date_answer = value;
          } else if (question.question_type === 'ranking') {
            answerData.ranking_answer = value;
          } else if (question.question_type === 'likert') {
            answerData.likert_answers = value;
          } else if (question.question_type === 'user_select') {
            answerData.user_answers = value;
          } else if (question.question_type === 'project_select') {
            answerData.project_answers = value;
          }

          return answerData;
        });

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/submit/`,
        { answers: formattedAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmitted(true);
      showToastNotification('Survey submitted successfully!');
    } catch (error) {
      console.error('Error submitting survey:', error);
      showToastNotification(error.response?.data?.error || 'Error submitting survey');
    } finally {
      setSubmitting(false);
    }
  };

  const updateAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    if (errors[questionId]) {
      setErrors({ ...errors, [questionId]: null });
    }
  };

  const handleChoiceChange = (questionId, optionId, isMultiple) => {
    if (isMultiple) {
      const current = answers[questionId] || [];
      if (current.includes(optionId)) {
        updateAnswer(questionId, current.filter(id => id !== optionId));
      } else {
        updateAnswer(questionId, [...current, optionId]);
      }
    } else {
      updateAnswer(questionId, optionId);
    }
  };

  const handleRankingMove = (questionId, fromIndex, toIndex) => {
    const newOrder = [...answers[questionId]];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    updateAnswer(questionId, newOrder);
  };

  const getRatingIcon = (symbol, filled) => {
    const icons = {
      star: filled ? <StarIcon /> : <StarBorderIcon />,
      heart: filled ? <HeartIcon /> : <HeartBorderIcon />,
      flag: filled ? <FlagIcon /> : <FlagBorderIcon />,
      thumb: filled ? <ThumbIcon /> : <ThumbBorderIcon />,
      smile: filled ? <SmileIcon /> : <SmileBorderIcon />,
      ribbon: filled ? <RibbonIcon /> : <RibbonBorderIcon />,
      number: null
    };
    return icons[symbol];
  };

  const renderQuestion = (question) => {
    if (question.question_type === 'section') {
      return (
        <div key={question.id} className="survey-section">
          <h2>{question.question_text}</h2>
          {question.description && <p>{question.description}</p>}
        </div>
      );
    }

    return (
      <div key={question.id} className={`question-card ${errors[question.id] ? 'has-error' : ''}`}>
        <div className="question-header">
          <span className="question-text">
            {question.question_text}
            {question.is_required && <span className="required-star">*</span>}
          </span>
        </div>
        {question.description && (
          <p className="question-description">{question.description}</p>
        )}

        <div className="question-answer">
          {question.question_type === 'choice' && (
            <div className="choice-options">
              {question.options.map(option => (
                <label key={option.id} className="choice-option">
                  <input
                    type={question.allow_multiple ? 'checkbox' : 'radio'}
                    name={`question_${question.id}`}
                    checked={question.allow_multiple
                      ? (answers[question.id] || []).includes(option.id)
                      : answers[question.id] === option.id
                    }
                    onChange={() => handleChoiceChange(question.id, option.id, question.allow_multiple)}
                  />
                  <span>{option.text}</span>
                </label>
              ))}
            </div>
          )}

          {question.question_type === 'text' && (
            question.text_multiline ? (
              <textarea
                value={answers[question.id] || ''}
                onChange={e => updateAnswer(question.id, e.target.value)}
                placeholder="Enter your answer"
                rows={4}
              />
            ) : (
              <input
                type="text"
                value={answers[question.id] || ''}
                onChange={e => updateAnswer(question.id, e.target.value)}
                placeholder="Enter your answer"
              />
            )
          )}

          {question.question_type === 'rating' && (
            <div className="rating-input">
              {question.rating_symbol === 'number' ? (
                <div className="rating-numbers">
                  {Array.from({ length: question.rating_levels }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      className={`rating-number ${answers[question.id] === num ? 'selected' : ''}`}
                      onClick={() => updateAnswer(question.id, num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rating-icons">
                  {Array.from({ length: question.rating_levels }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      className={`rating-icon ${answers[question.id] >= num ? 'filled' : ''}`}
                      onClick={() => updateAnswer(question.id, num)}
                    >
                      {getRatingIcon(question.rating_symbol, answers[question.id] >= num)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {question.question_type === 'nps' && (
            <div className="nps-input">
              <div className="nps-labels">
                <span>{question.nps_min_label}</span>
                <span>{question.nps_max_label}</span>
              </div>
              <div className="nps-scale">
                {Array.from({ length: 11 }, (_, i) => i).map(num => (
                  <button
                    key={num}
                    className={`nps-button ${answers[question.id] === num ? 'selected' : ''} ${num <= 6 ? 'detractor' : num <= 8 ? 'passive' : 'promoter'}`}
                    onClick={() => updateAnswer(question.id, num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {question.question_type === 'date' && (
            <input
              type="date"
              value={answers[question.id] || ''}
              onChange={e => updateAnswer(question.id, e.target.value)}
            />
          )}

          {question.question_type === 'ranking' && (
            <div className="ranking-input">
              {(answers[question.id] || []).map((optionId, index) => {
                const option = question.options.find(o => o.id === optionId);
                return (
                  <div key={optionId} className="ranking-item">
                    <span className="rank-number">{index + 1}</span>
                    <span className="rank-text">{option?.text}</span>
                    <div className="rank-controls">
                      <button
                        disabled={index === 0}
                        onClick={() => handleRankingMove(question.id, index, index - 1)}
                      >
                        ↑
                      </button>
                      <button
                        disabled={index === answers[question.id].length - 1}
                        onClick={() => handleRankingMove(question.id, index, index + 1)}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {question.question_type === 'likert' && (
            <div className="likert-input">
              <table className="likert-table">
                <thead>
                  <tr>
                    <th></th>
                    {question.options.map(option => (
                      <th key={option.id}>{option.text}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {question.statements.map(statement => (
                    <tr key={statement.id}>
                      <td className="statement-text">{statement.text}</td>
                      {question.options.map(option => (
                        <td key={option.id}>
                          <input
                            type="radio"
                            name={`likert_${question.id}_${statement.id}`}
                            checked={(answers[question.id] || {})[statement.id] === option.id}
                            onChange={() => {
                              const newLikertAnswers = {
                                ...(answers[question.id] || {}),
                                [statement.id]: option.id
                              };
                              updateAnswer(question.id, newLikertAnswers);
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {question.question_type === 'file' && (
            <div className="file-input">
              <label className="file-upload-label">
                <UploadIcon />
                <span>Click to upload a file</span>
                <input
                  type="file"
                  onChange={e => updateAnswer(question.id, e.target.files[0])}
                />
              </label>
              {answers[question.id] && (
                <div className="file-selected">
                  {answers[question.id].name}
                  <button onClick={() => updateAnswer(question.id, null)}>
                    <CloseIcon />
                  </button>
                </div>
              )}
            </div>
          )}

          {question.question_type === 'user_select' && (
            <div className="user-select-input">
              <div className="select-header">
                <PersonIcon />
                <span>Select users (multiple selection allowed)</span>
                {(answers[question.id] || []).length > 0 && (
                  <span className="selected-count">
                    {(answers[question.id] || []).length} selected
                  </span>
                )}
              </div>
              <div className="select-search">
                <SearchIcon className="search-icon" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQueries[`user_${question.id}`] || ''}
                  onChange={e => setSearchQueries({
                    ...searchQueries,
                    [`user_${question.id}`]: e.target.value
                  })}
                />
                {searchQueries[`user_${question.id}`] && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQueries({
                      ...searchQueries,
                      [`user_${question.id}`]: ''
                    })}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
              <div className="select-options">
                {selectOptions.users
                  .filter(user => {
                    const query = (searchQueries[`user_${question.id}`] || '').toLowerCase();
                    if (!query) return true;
                    return (
                      user.name?.toLowerCase().includes(query) ||
                      user.email?.toLowerCase().includes(query)
                    );
                  })
                  .map(user => (
                    <label key={user.id} className="select-option">
                      <input
                        type="checkbox"
                        checked={(answers[question.id] || []).includes(user.id)}
                        onChange={e => {
                          const current = answers[question.id] || [];
                          if (e.target.checked) {
                            updateAnswer(question.id, [...current, user.id]);
                          } else {
                            updateAnswer(question.id, current.filter(id => id !== user.id));
                          }
                        }}
                      />
                      <span className="select-option-name">{user.name}</span>
                      <span className="select-option-email">{user.email}</span>
                    </label>
                  ))}
                {selectOptions.users.length === 0 && (
                  <div className="no-options">No users available</div>
                )}
                {selectOptions.users.length > 0 &&
                  selectOptions.users.filter(user => {
                    const query = (searchQueries[`user_${question.id}`] || '').toLowerCase();
                    if (!query) return true;
                    return user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query);
                  }).length === 0 && (
                  <div className="no-options">No users matching "{searchQueries[`user_${question.id}`]}"</div>
                )}
              </div>
            </div>
          )}

          {question.question_type === 'project_select' && (
            <div className="project-select-input">
              <div className="select-header">
                <ProjectIcon />
                <span>Select projects (multiple selection allowed)</span>
                {(answers[question.id] || []).length > 0 && (
                  <span className="selected-count">
                    {(answers[question.id] || []).length} selected
                  </span>
                )}
              </div>
              <div className="select-search">
                <SearchIcon className="search-icon" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQueries[`project_${question.id}`] || ''}
                  onChange={e => setSearchQueries({
                    ...searchQueries,
                    [`project_${question.id}`]: e.target.value
                  })}
                />
                {searchQueries[`project_${question.id}`] && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQueries({
                      ...searchQueries,
                      [`project_${question.id}`]: ''
                    })}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
              <div className="select-options">
                {selectOptions.projects
                  .filter(project => {
                    const query = (searchQueries[`project_${question.id}`] || '').toLowerCase();
                    if (!query) return true;
                    return (
                      project.name?.toLowerCase().includes(query) ||
                      project.code?.toLowerCase().includes(query)
                    );
                  })
                  .map(project => (
                    <label key={project.id} className="select-option">
                      <input
                        type="checkbox"
                        checked={(answers[question.id] || []).includes(project.id)}
                        onChange={e => {
                          const current = answers[question.id] || [];
                          if (e.target.checked) {
                            updateAnswer(question.id, [...current, project.id]);
                          } else {
                            updateAnswer(question.id, current.filter(id => id !== project.id));
                          }
                        }}
                      />
                      <span className="select-option-name">{project.name}</span>
                      {project.code && <span className="select-option-code">{project.code}</span>}
                    </label>
                  ))}
                {selectOptions.projects.length === 0 && (
                  <div className="no-options">No projects available</div>
                )}
                {selectOptions.projects.length > 0 &&
                  selectOptions.projects.filter(project => {
                    const query = (searchQueries[`project_${question.id}`] || '').toLowerCase();
                    if (!query) return true;
                    return project.name?.toLowerCase().includes(query) || project.code?.toLowerCase().includes(query);
                  }).length === 0 && (
                  <div className="no-options">No projects matching "{searchQueries[`project_${question.id}`]}"</div>
                )}
              </div>
            </div>
          )}
        </div>

        {errors[question.id] && (
          <div className="question-error">{errors[question.id]}</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="survey-fill-loading">Loading survey...</div>;
  }

  if (!survey) {
    return <div className="survey-fill-error">Survey not found or access denied</div>;
  }

  if (submitted) {
    return (
      <div className="survey-submitted">
        <div className="submitted-content">
          <CheckIcon className="success-icon" />
          <h2>Thank you!</h2>
          <p>Your response has been recorded.</p>
          <Button onClick={() => navigate('/surveys')}>
            Back to Surveys
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-fill-container">
      {/* Header */}
      <div className="fill-header">
        <button className="back-btn" onClick={() => navigate('/surveys')}>
          <BackIcon />
        </button>
        <div className="survey-title-section">
          <h1>{survey.title}</h1>
          {survey.is_anonymous && <span className="anonymous-badge">Anonymous Survey</span>}
        </div>
      </div>

      {/* Survey Description */}
      {survey.description && (
        <div className="survey-description-card">
          <p>{survey.description}</p>
        </div>
      )}

      {/* Questions */}
      <div className="questions-container">
        {survey.questions.map(question => renderQuestion(question))}
      </div>

      {/* Submit Button */}
      <div className="submit-section">
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting...' : <><SendIcon /> Submit Survey</>}
        </Button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default SurveyFill;
